import { MenuItem } from "@chakra-ui/react";
import { useEntityConfirmDelete } from "features/entities/EntityDelete";
import { InvestorEntityTabProps } from "features/investor-entities/InvestorEntityTabProps";
import { useInvestorListSuspense } from "gen/endpoints/investor/investor";
import { useInvestorEntityRetrieveSuspense } from "gen/endpoints/investor-entity/investor-entity";
import { InvestorReadData } from "gen/model/investorReadData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useMatches, useNavigate, useParams } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { PageTabs } from "shared/components/layout/PageTabs";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useCurrentPageTabId } from "shared/navigation/useCurrentPageTabId";

export const InvestorEntityPage: React.FC = () => {
  const { t } = useTranslation(["common", "commitment", "investorEntity"]);
  const navigate = useNavigate();
  const currentPageTabId = useCurrentPageTabId();
  const { fundFamilyId, investorId } = useParams();
  const { investorEntityId } = useRequiredStringParams();

  const { data: investorEntity } =
    useInvestorEntityRetrieveSuspense(investorEntityId);

  const { data: investors } = useInvestorListSuspense({
    investor_entity_ids: [investorEntityId],
  });

  const matches = useMatches();
  const isSetup = matches.some((it) => it.id === "fundFamilySetup");

  const backButtonProps = getBackButtonProps(
    t,
    isSetup,
    fundFamilyId,
    investorId,
  );

  const confirmEntityDelete = useEntityConfirmDelete();
  const onClickInvestorDelete = async () => {
    const success = await confirmEntityDelete(investorEntityId);
    if (success) {
      navigate(backButtonProps.path);
    }
  };

  const tabProps: InvestorEntityTabProps = {
    investorEntityId,
  };

  return (
    <PageContent
      contentWidth="wide"
      isFullHeight={!currentPageTabId.endsWith("TabProfile")}
    >
      <PageHeader
        title={investorEntity.legal_name}
        backButton={backButtonProps}
        metadata={[
          {
            title: t("common:investors"),
            flattenIfSingle: true,
            children: investors.map((investor: InvestorReadData) => ({
              title: investor.name,
              to: rootRoute({})
                .investors({})
                .investor({ investorId: investor.id })
                .activity({}).$,
            })),
          },
          {
            title: t("common:investorEntity"),
          },
        ]}
        actions={
          <Restricted permission={PermissionTypeEnum.config}>
            <PageMenu>
              <MenuItem className="destructive" onClick={onClickInvestorDelete}>
                {t("common:delete")}
              </MenuItem>
            </PageMenu>
          </Restricted>
        }
      />
      <PageBody>
        <PageTabs context={tabProps} />
      </PageBody>
    </PageContent>
  );
};

const getBackButtonProps = (
  t: TFunction<["common"]>,
  isSetup: boolean,
  fundFamilyId?: string,
  investorId?: string,
) => {
  if (fundFamilyId) {
    const fundFamilyRoute = rootRoute({})
      .fundFamilies({})
      .fundFamily({ fundFamilyId });
    return isSetup
      ? {
          title: t("common:entities"),
          path: fundFamilyRoute.setup({}).entities({}).$,
        }
      : {
          // NOTE: The commitments table is currently the only way to get
          // to an investor entity from a fund family.
          title: t("common:commitments"),
          path: fundFamilyRoute.commitments({}).$,
        };
  }
  if (investorId) {
    return {
      title: t("common:investorEntities"),
      path: rootRoute({}).investors({}).investor({ investorId }).entities({}).$,
    };
  }
  return {
    path: rootRoute({}).entities({}).$,
    title: t("common:entities"),
  };
};
